<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div v-if="!loading" class="mt-4 mb-4 refund-page">
        <h3>Refusjon for ordre betalt via selvbetjeningskiosk eller kasse</h3>
        <div class="mt-4">{{getOrderTypeText()}} - Ordre #{{order.orderId}}</div>
        <!-- Backoffice (SUPER_ADMIN): no till in the loop, so the server does what the till does after a
             refund — grand total of returns and the electronic journal, on the till that took the order. -->
        <div v-if="backoffice" class="alert alert-warning mt-3 py-2" style="font-size:0.88rem">
          Refunderes til kortet i Viva uten at kunden er til stede. Refusjonen føres på kassen eller kiosken
          som tok imot ordren (retur i dagsoppgjøret, grand total retur og elektronisk journal), med deg som
          bruker. Kun superadmin.
        </div>

        <div v-if="step == 1">
          <div class="mt-5 mb-0">
            <button type="button" class="bo_btn" @click="selectAllOrRemoveAll"> {{selectAllOrRemoveAllButtonText()}} </button>
          </div>
          <hr class="mb-0"/>
          <div v-for="refundCartItem in refundCartItems" :key="refundCartItem.id" :id="refundCartItem.id" class="refundCartItem">
            <div class="d-flex align-items-center justify-content-between clickable-row" @click="updateSelectedCartItems(refundCartItem)">
              <div class="d-flex align-items-center">
                <input type="checkbox" :name="refundCartItem.id" :value="refundCartItem.id" :checked="selectedCartItemsContains(refundCartItem)" @click.stop="updateSelectedCartItems(refundCartItem)" class="checkbox" />
                <div>
                  <div>
                    {{ refundCartItem.itemName }}
                  </div>
                  <div v-if="refundCartItem.itemSubselection">
                    {{ refundCartItem.itemSubselection.name }}
                  </div>
                  <div v-if="refundCartItem.spicyLevel != ''">
                    {{ refundCartItem.spicyLevel}}
                  </div>
                  <div v-if="refundCartItem.comment != ''">
                    {{ refundCartItem.comment }}
                  </div>
                </div>
              </div>
              <div>
                {{ displayTwoDecimals(getPriceWithVat(refundCartItem)) }}
              </div>
            </div>
          </div>
          <div v-if="order.tipsAmount && order.tipsAmount > 0" class="mt-2">
            <div class="d-flex align-items-center justify-content-between clickable-row" @click="updateRefundedTipsAmount()">
              <div class="d-flex align-items-center">
                <input type="checkbox" :name="'tips_' + order.orderId" :value="'tips_' + order.orderId"
                       :checked="refundedTipsAmount > 0" @click.stop="updateRefundedTipsAmount()"
                       class="checkbox" />
                <div> Tips </div>
              </div>
              <div>
                {{ displayTwoDecimals(order.tipsAmount) }}
              </div>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <button type="button" class="bo_btn" @click="step=2" :disabled="disableNextBtn()">Neste</button>
          </div>
        </div>

        <div v-if="step == 2">
          <hr class="mb-0"/>
          <div v-for="selectedCartItem in selectedCartItems" :key="selectedCartItem.id" :id="selectedCartItem.id" class="refundCartItem">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div>
                  <div>
                    -1 x {{selectedCartItem.itemName}}
                  </div>
                  <div v-if="selectedCartItem.itemSubselection">
                    {{ selectedCartItem.itemSubselection.name }}
                  </div>
                  <div v-if="selectedCartItem.spicyLevel != ''">
                    {{ selectedCartItem.spicyLevel}}
                  </div>
                  <div v-if="selectedCartItem.comment != ''">
                    {{ selectedCartItem.comment }}
                  </div>
                </div>
              </div>
              <div>
                -{{ displayTwoDecimals(getPriceWithVat(selectedCartItem)) }}
              </div>
            </div>
          </div>
          <div v-if="refundedTipsAmount > 0" class="mt-2 mb-3">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div> -Tips </div>
              </div>
              <div>
                -{{ displayTwoDecimals(refundedTipsAmount) }}
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span style="font-weight: bold;">Totalt</span>
            </div>
            <div>
              <span style="font-weight: bold;color: red">-{{ displayTwoDecimals(getTotalRefundAmount()) }}</span>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <button type="button" class="bo_btn" @click="step=1">Tilbake</button>
              </div>
              <div>
                <button type="button" class="bo_btn" @click="refund">Refunder</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import OrderService from "@/services/order/OrderService";
import OrderUtil from "@/util/OrderUtil";
import VatUtil from "@/util/VatUtil";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import VivaService from "@/services/payment/VivaService";
import MathUtil from "@/util/MathUtil";
import RefundJournal from "@/util/RefundJournal";

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  // true on the Backoffice routes (/vivaTerminalRefund…, SUPER_ADMIN): the page keeps Backoffice's
  // navigation and refunds through the endpoint that also books the refund on the till.
  props: {
    backoffice: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: true,
      step: 1,

      vat: {},
      orderDocumentId: '',
      order: {
        type: "order",
        merchant: '',
        orderId: '',
        orderType: '',
        orderStatus: '',
        totalAmount: 0,
        description: '',
        pickupOrDeliveryTime: '',
        mobileNumber: '',
        name: '',
        nameOnDoorbell: '',
        street: '',
        houseNumber: '',
        floor: '',
        zipCode: '',
        city: '',
        orderedTime: '',
        modifiedTime: '',
        servedBy: '',
        channel: '',
        payments: [],
        vats: [],
        totalVat: 0,
        cartItemsJson: []
      },
      refundCartItems: [],
      refundedTipsAmount : 0,
      selectedCartItems: []
    };
  },
  mounted() {
    if(!this.backoffice){
      // Inside the till's WebView: the till has its own chrome.
      document.getElementById('nav').hidden = true;
      document.getElementById('footer').hidden = true;
      document.getElementById('scrollTopBtn').hidden = true;
    }

    this.orderDocumentId = this.$route.query.orderDocumentId;
    OrderService.getDataForOrderDetailGui(this.orderDocumentId).then(
        (response) => {
          let dataForGui = response.data;
          //console.log('dataForGui: ' + JSON.stringify(dataForGui));
          this.order = dataForGui.order;
          this.vat = dataForGui.vat;
          this.refundCartItems = this.getRefundCartItems();
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
    );
  },
  methods:{
    getRefundCartItems(){
      let refundCartItems = [];
      let cartItems = this.order.cartItems;
      console.log('cartItems: ' + JSON.stringify(cartItems));
      for (let i = 0; i < cartItems.length; i++) {
        let cartItem = cartItems[i];
        for(let x = 1; x <= cartItem.count; x++){
          let refundCartItem = {
            originalCartItemId : cartItem.id,
            id: cartItem.id + '_' + x, //make it unique
            itemId: cartItem.itemId,
            itemName: cartItem.itemName + OrderUtil.shareSuffix(cartItem),
            shareAmount: cartItem.shareAmount,
            shareNumerator: cartItem.shareNumerator,
            shareDenominator: cartItem.shareDenominator,
            shareOfCount: cartItem.shareOfCount,
            itemType: cartItem.itemType,
            price: cartItem.price,
            discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
            count: 1,
            itemSubselection: cartItem.itemSubselection,
            spicyLevel: cartItem.spicyLevel,
            comment: cartItem.comment,
            createdDate: cartItem.createdDate
          };
          refundCartItems.push(refundCartItem);

          //subselection addOns
          if(refundCartItem.itemSubselection){
            let subselectionAddOnCartItems =  refundCartItem.itemSubselection.addOnCartItems;
            if(subselectionAddOnCartItems){
              for(let a = 0; a < subselectionAddOnCartItems.length; a++){
                let addOnCartItem = subselectionAddOnCartItems[a];
                let addOn = addOnCartItem.addOn;
                for(let y = 1; y <= addOnCartItem.count; y++){
                  //Only if it isn't free
                  if(addOnCartItem.price > 0){
                    let addOnRefundCartItem = {
                      originalCartItemId : cartItem.id, //The id of the parent cartItem the addon belongs to.
                      id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                      itemId: addOnCartItem.addOnId,
                      itemName: addOn.name,
                      itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                      price: addOnCartItem.price,
                      discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                      count: 1,
                      createdDate: cartItem.createdDate
                    };
                    refundCartItems.push(addOnRefundCartItem);
                  }
                }
              }
            }
          }

          //item addOns
          if(cartItem.addOnCartItems && cartItem.addOnCartItems.length > 0){
            for (let a = 0; a < cartItem.addOnCartItems.length; a++) {
              let addOnCartItem = cartItem.addOnCartItems[a];
              let addOn = addOnCartItem.addOn;
              for(let y = 1; y <= addOnCartItem.count; y++){
                //Only if it isn't free
                if(addOnCartItem.price > 0){
                  let addOnRefundCartItem = {
                    originalCartItemId : cartItem.id, //The id of the parent cartItem the addon belongs to.
                    id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                    itemId: addOnCartItem.addOnId,
                    itemName: addOn.name,
                    itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                    price: addOnCartItem.price,
                    discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                    count: 1,
                    createdDate: cartItem.createdDate
                  };
                  refundCartItems.push(addOnRefundCartItem);
                }
              }
            }
          }
        }
      }
      return refundCartItems;
    },

    getOrderTypeText(){
      return OrderUtil.getOrderTypeText(this.order.orderType).toUpperCase();
    },
    getPriceWithVat(cartItem){
      if(OrderUtil.isShare(cartItem) && !(this.order.manualDiscount > 0)){
        return OrderUtil.shareAmount(cartItem); //a share as the till worked it out, never rounded to whole kroner
      }
      // Check if this order has manual discount - if so, use proportional calculation
      if(this.order.manualDiscount && this.order.manualDiscount > 0){
        return this.getPriceWithVatAndManualDiscount(cartItem);
      }
      
      // Original logic for orders without manual discount
      let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
      let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
      if(cartItem.discountPercent == 0){
        return Math.round(priceWithVat); //make it exact as it is in order description.
      } else {
        return MathUtil.roundTwoDecimals(priceWithVat);
      }
    },
    getPriceWithVatAndManualDiscount(cartItem){
      // For orders with manual discount, calculate proportional price based on final amount paid
      let totalPaidAmount = this.order.toPayAmount || this.order.totalAmount;
      
      // Calculate expected total without tips and manual discount
      let expectedProductTotal = totalPaidAmount - (this.order.tipsAmount || 0);
      
      // Calculate sum of all base prices (without manual discount) for all refund cart items
      let totalBasePrice = 0;
      this.refundCartItems.forEach(item => {
        let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, item.itemType);
        let basePriceWithVat = OrderUtil.getPriceWithVat(vatRate, item.price) * (100 - item.discountPercent) / 100;
        totalBasePrice += basePriceWithVat;
      });
      
      // If there's only one item, it gets the full expected amount
      if(this.refundCartItems.length === 1){
        return MathUtil.roundTwoDecimals(expectedProductTotal);
      }
      
      // Find which item index this is
      let itemIndex = this.refundCartItems.findIndex(item => item.id === cartItem.id);
      if(itemIndex === -1){
        // Fallback to original calculation
        let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
        let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
        return MathUtil.roundTwoDecimals(priceWithVat);
      }
      
      // For multiple items, distribute proportionally but adjust last item for exact match
      let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
      let basePrice = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
      let proportionalPrice = (basePrice / totalBasePrice) * expectedProductTotal;
      
      // If this is the last item, calculate what's left to ensure exact match
      if(itemIndex === this.refundCartItems.length - 1){
        let alreadyAllocated = 0;
        for(let i = 0; i < this.refundCartItems.length - 1; i++){
          let otherItem = this.refundCartItems[i];
          let otherVatRate = VatUtil.getVatRate(this.vat, this.order.orderType, otherItem.itemType);
          let otherBasePrice = OrderUtil.getPriceWithVat(otherVatRate, otherItem.price) * (100 - otherItem.discountPercent) / 100;
          let otherProportional = (otherBasePrice / totalBasePrice) * expectedProductTotal;
          alreadyAllocated += MathUtil.roundTwoDecimals(otherProportional);
        }
        return MathUtil.roundTwoDecimals(expectedProductTotal - alreadyAllocated);
      }
      
      return MathUtil.roundTwoDecimals(proportionalPrice);
    },
    disableNextBtn(){
      let disabled = this.selectedCartItems.length == 0;
      if(this.order.tipsAmount && this.order.tipsAmount > 0 && disabled){
        if(this.refundedTipsAmount == 0){
          disabled = true;
        } else {
          disabled = false;
        }
      }
      return disabled;
    },
    selectAllOrRemoveAllButtonText(){
      if(this.order.tipsAmount && this.order.tipsAmount > 0){
        if(this.selectedCartItems.length == this.refundCartItems.length && this.refundedTipsAmount > 0){
          return 'Fjern alle valgte';
        }else {
          return 'Velg alle';
        }
      } else {
        if(this.selectedCartItems.length == this.refundCartItems.length){
          return 'Fjern alle valgte';
        }else {
          return 'Velg alle';
        }
      }
    },
    selectAllOrRemoveAll(){
      //console.log('BEFORE: ' + JSON.stringify(this.selectedCartItems));
      if(this.selectedCartItems.length == this.refundCartItems.length){
        //Remove all
        this.selectedCartItems = [];
        this.refundedTipsAmount = 0;
      }else {
        //Add all
        for (let i = 0; i < this.refundCartItems.length; i++) {
          if(!this.selectedCartItemsContains(this.refundCartItems[i])){
            this.selectedCartItems.push(this.refundCartItems[i]);
          }
        }
        this.refundedTipsAmount = this.order.tipsAmount ? this.order.tipsAmount : 0;
      }
      //console.log('AFTER: ' + JSON.stringify(this.selectedCartItems));
    },
    updateRefundedTipsAmount(){
      if(this.refundedTipsAmount > 0){
        this.refundedTipsAmount = 0;
      } else {
        this.refundedTipsAmount = this.order.tipsAmount;
      }
    },
    updateSelectedCartItems(refundCartItem){
      console.log('BEFORE: ' + JSON.stringify(this.selectedCartItems));
      if(this.selectedCartItemsContains(refundCartItem)){
        //Already selected, remove it
        for (let i = 0; i < this.selectedCartItems.length; i++) {
          if (this.selectedCartItems[i].id == refundCartItem.id) {
            this.selectedCartItems.splice(i, 1);
            break;
          }
        }
      }else {
        //Add it
        this.selectedCartItems.push(refundCartItem);
      }
      console.log('AFTER: ' + JSON.stringify(this.selectedCartItems));
    },
    selectedCartItemsContains(cartItem){
      for (let i = 0; i < this.selectedCartItems.length; i++) {
        let selectedCartItem = this.selectedCartItems[i];
        if(selectedCartItem.id == cartItem.id){
          return true;
        }
      }
      return false;
    },
    getTotalRefundAmount(){
      let totalPricesInCart = 0.00;
      for (let i = 0; i < this.selectedCartItems.length; i++) {
        let selectedCartItem = this.selectedCartItems[i];
        let cartItemPrice = this.getPriceWithVat(selectedCartItem);
        console.log(cartItemPrice);
        totalPricesInCart = totalPricesInCart + cartItemPrice;
      }

      totalPricesInCart = totalPricesInCart + this.refundedTipsAmount;
      return totalPricesInCart;
    },
    getTransactionId(){
      let payments = this.order.payments;
      for (let i = 0; i < payments.length; i++) {
        let payment = payments[i];
        if(payment.transactionId){
          return payment.transactionId;
        }
      }
      return null;
    },
    async refund(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()),
        message: 'Vil du refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()) + ' til kunden?',
        okButton: 'Refunder',
        cancelButton: 'Avbryt',
        spreadButtons: true,
      })
      if(ok) {
        this.loading = true;

        // Create a copy of selectedCartItems with base prices (without manual discount applied)
        const refundedCartItemsWithVat = this.selectedCartItems.map(cartItem => {
          if (OrderUtil.isShare(cartItem)) {
            // A share at its own amount, as the till sold it: its price rounded to whole kroner refunded a third
            // of a 499 bottle as 166 and left the VAT and the category short.
            return { ...cartItem, price: OrderUtil.shareAmount(cartItem) };
          }
          // Calculate VAT-inclusive price based on the original base price (without manual discount)
          let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
          let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
          let finalPrice = cartItem.discountPercent == 0 ? Math.round(priceWithVat) : MathUtil.roundTwoDecimals(priceWithVat);

          return {
            ...cartItem,
            price: finalPrice
          };
        });

        // Viva refunds are processed via backend API - no card tap needed
        const refundRequest = this.backoffice
            ? VivaService.terminalRefund(this.getTransactionId(), this.getTotalRefundAmount(), refundedCartItemsWithVat, this.refundedTipsAmount, this.orderDocumentId)
            : VivaService.refund(this.getTransactionId(), this.getTotalRefundAmount(), refundedCartItemsWithVat, this.refundedTipsAmount, this.orderDocumentId);
        refundRequest.then(
            (response) => {
              if(response.data === 'SUCCEEDED'){
                this.incrementGrandTotalRefund(this.getTotalRefundAmount(), this.orderDocumentId);
                if(!this.backoffice){ // from Backoffice the server journals it (VivaTerminalRefund)
                  RefundJournal.send({
                    order: this.order,
                    orderDocumentId: this.orderDocumentId,
                    rows: this.selectedCartItems,
                    priceOf: row => this.getPriceWithVat(row),
                    paidBack: [RefundJournal.cardLeg(this.order, this.getTransactionId(), this.getTotalRefundAmount())],
                    tips: this.refundedTipsAmount,
                  });
                }
                this.goToOrderDetailPage();
              } else {
                this.showRefundFailedMessage(VivaService.refundFailedMessage(response.data, this.getTotalRefundAmount()));
                this.loading = false;
              }
            },
            (error) => {
              console.log(error);
              this.showRefundFailedMessage();
              this.loading = false;
            }
        );
      }
    },
    incrementGrandTotalRefund(refundAmount, orderDocumentId){
      // From Backoffice the server has already added it to the till's grand total; doing it here as well
      // would count it twice if the page ever ran inside a till.
      if(this.backoffice) return;
      if(window.JSBridge && window.JSBridge.incrementGrandTotalRefund) {
        window.JSBridge.incrementGrandTotalRefund(refundAmount, orderDocumentId);
      }
    },
    goToOrderDetailPage(){
      this.$router.push({ path: 'orderDetail', query: {
          orderDocumentId: this.orderDocumentId,
          showRefundInfo: 'false'
        }
      });
    },
    async showRefundFailedMessage(message){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refusjonen mislykket',
        message: message || 'Det skjedde en feil og refusjonen ble ikke fullført.',
        okButton: 'OK',
        cancelButton: 'NOT_SET'
      })
      if(ok) {
        //Do nothing
      }
    },
    displayTwoDecimals(num){
      if(num > 0 || num < 0){
        return MathUtil.displayTwoDecimals(num);
      }else {
        return num;
      }
    }
  }
}
</script>

<style scoped>
  .refundCartItem{
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: var(--bo-muted-2);
    border-bottom: 1px  solid var(--bo-muted-2);
  }

  .checkbox{
    width: 25px;
    height: 25px;
    margin-right: 10px;
  }
</style>
